import { gql } from '@apollo/client';

// Auth Queries
export const ME = gql`
  query Me {
    me {
      id
      username
      email
      firstName
      lastName
      displayName
      userType
      phone
      profilePicture
      coverImage
      bio
      city
      country
      isVerified
      phoneVerified
      emailVerified
      maxActiveListings
      activeListingsCount
      remainingListings
      canPostAd
      dateJoined
    }
  }
`;

export const PUBLIC_PROFILE = gql`
  query PublicProfile($username: String!) {
    publicProfile(username: $username) {
      id
      username
      displayName
      userType
      bio
      profilePicture
      coverImage
      city
      country
      isVerified
      isSellerVerified
      dateJoined
      totalAds
      activeAds
      averageRating
    }
  }
`;

// Category Queries
export const GET_CATEGORIES = gql`
  query GetCategories {
    rootCategories {
      id
      name
      slug
      icon
      image
      hasChildren
      childrenList {
        id
        name
        slug
        icon
      }
    }
  }
`;

export const GET_CATEGORY_TREE = gql`
  query GetCategoryTree {
    categoryTree {
      id
      name
      slug
      icon
      level
      parent {
        id
      }
    }
  }
`;

// Ad Queries
export const GET_AD = gql`
  query GetAd($slug: String!) {
    adBySlug(slug: $slug) {
      id
      title
      slug
      description
      price
      priceType
      condition
      brand
      modelName
      year
      location
      city
      state
      deliveryMethod
      deliveryDetails
      contactPhone
      contactWhatsapp
      contactEmail
      showContactPublicly
      status
      isActive
      viewCount
      favoriteCount
      publishedAt
      isFeatured
      isTrending
      isOwner
      isFavorited
      categoryPath
      category {
        id
        slug
        name
      }
      user {
        id
        username
        displayName
        profilePicture
        isVerified
        dateJoined
      }
      imagesList {
        id
        url
        caption
        isPrimary
      }
    }
  }
`;

export const GET_FEATURED_ADS = gql`
  query GetFeaturedAds($limit: Int) {
    featuredAds(limit: $limit) {
      id
      title
      slug
      price
      priceType
      primaryImage
      location
      city
      condition
      isFeatured
      publishedAt
      user {
        id
        username
        displayName
        profilePicture
      }
    }
  }
`;

export const GET_TRENDING_ADS = gql`
  query GetTrendingAds($limit: Int) {
    trendingAds(limit: $limit) {
      id
      title
      slug
      price
      priceType
      primaryImage
      location
      city
      condition
      isTrending
      publishedAt
      user {
        id
        username
        displayName
        profilePicture
      }
    }
  }
`;

export const GET_NEW_ADS = gql`
  query GetNewAds($limit: Int, $offset: Int) {
    newAds(limit: $limit, offset: $offset) {
      id
      title
      slug
      price
      priceType
      primaryImage
      location
      city
      condition
      publishedAt
      user {
        id
        username
        displayName
        profilePicture
      }
    }
  }
`;

export const GET_CATEGORY_ADS = gql`
  query GetCategoryAds($categorySlug: String!, $limit: Int, $offset: Int) {
    categoryAds(categorySlug: $categorySlug, limit: $limit, offset: $offset) {
      id
      title
      slug
      price
      priceType
      primaryImage
      location
      city
      condition
      publishedAt
      user {
        id
        username
        displayName
        profilePicture
      }
    }
  }
`;

export const SEARCH_ADS = gql`
  query SearchAds(
    $query: String!
    $category: String
    $city: String
    $minPrice: Float
    $maxPrice: Float
    $condition: String
    $limit: Int
  ) {
    searchAds(
      query: $query
      category: $category
      city: $city
      minPrice: $minPrice
      maxPrice: $maxPrice
      condition: $condition
      limit: $limit
    ) {
      id
      title
      slug
      price
      priceType
      primaryImage
      location
      city
      condition
      publishedAt
      user {
        id
        username
        displayName
        profilePicture
      }
    }
  }
`;

// Dashboard Queries
export const GET_MY_ADS = gql`
  query GetMyAds($status: String, $limit: Int, $offset: Int) {
    myAds(status: $status, limit: $limit, offset: $offset) {
      id
      title
      slug
      price
      priceType
      status
      isActive
      viewCount
      favoriteCount
      publishedAt
      createdAt
      imagesList {
        id
        url
        isPrimary
      }
    }
  }
`;

export const GET_MY_FAVORITES = gql`
  query GetMyFavorites {
    myFavorites {
      id
      title
      slug
      price
      priceType
      primaryImage
      location
      city
      publishedAt
    }
  }
`;

export const GET_MY_AD_STATS = gql`
  query GetMyAdStats {
    myAdStats
  }
`;

// Rating Queries
export const GET_USER_RATINGS = gql`
  query GetUserRatings($username: String!) {
    userRatings(username: $username) {
      id
      score
      review
      createdAt
      ratedBy {
        id
        username
        displayName
        profilePicture
      }
      response {
        message
        createdAt
      }
    }
  }
`;

export const GET_USER_RATING_SUMMARY = gql`
  query GetUserRatingSummary($username: String!) {
    userRatingSummary(username: $username) {
      averageRating
      totalRatings
      ratingDistribution
    }
  }
`;

export const GET_USER_ADS = gql`
  query GetUserAds($username: String!, $limit: Int, $offset: Int) {
    userAds(username: $username, limit: $limit, offset: $offset) {
      id
      title
      slug
      price
      priceType
      condition
      primaryImage
      location
      city
      status
      isActive
      isFeatured
      viewCount
      favoriteCount
      publishedAt
      category {
        id
        slug
        name
      }
    }
  }
`;

// Boost Queries
export const GET_BOOST_PACKAGES = gql`
  query GetBoostPackages {
    boostPackages {
      id
      name
      description
      packageType
      durationDays
      price
      features
      isActive
    }
  }
`;

export const GET_MY_BOOST_REQUESTS = gql`
  query GetMyBoostRequests($status: String) {
    myBoostRequests(status: $status) {
      id
      ad {
        id
        title
        slug
        primaryImage
      }
      package {
        id
        name
        price
        durationDays
      }
      durationDays
      amount
      status
      requestedAt
      paymentProofUrl
      paymentUploadedAt
      transactionId
      approvedBy {
        id
        username
        displayName
      }
      approvedAt
      rejectionReason
      boostStartedAt
      boostEndsAt
    }
  }
`;

export const GET_ALL_BOOST_REQUESTS = gql`
  query GetAllBoostRequests($status: String) {
    allBoostRequests(status: $status) {
      id
      ad {
        id
        title
        slug
        primaryImage
      }
      user {
        id
        username
        displayName
        email
        phone
      }
      package {
        id
        name
        price
        durationDays
      }
      durationDays
      amount
      status
      requestedAt
      paymentProofUrl
      paymentUploadedAt
      transactionId
      approvedBy {
        id
        username
        displayName
      }
      approvedAt
      rejectionReason
      boostStartedAt
      boostEndsAt
      notes
    }
  }
`;

// Video Ads Queries (Story Ads)
export const GET_VIDEO_ADS = gql`
  query GetVideoAds($limit: Int, $category: String) {
    videoAds(limit: $limit, category: $category) {
      id
      title
      slug
      price
      priceType
      primaryImage
      videoUrl
      videoThumbnail
      videoDuration
      hasVideo
      location
      city
      condition
      isFeatured
      isTrending
      publishedAt
      viewCount
      favoriteCount
      user {
        id
        username
        displayName
        profilePicture
        phone
        email
      }
      category {
        id
        name
        slug
      }
    }
  }
`;

// Notification Queries
export const GET_MY_NOTIFICATIONS = gql`
  query GetMyNotifications($unreadOnly: Boolean, $limit: Int) {
    myNotifications(unreadOnly: $unreadOnly, limit: $limit) {
      id
      notificationType
      title
      body
      link
      isRead
      createdAt
    }
  }
`;

export const GET_UNREAD_NOTIFICATION_COUNT = gql`
  query GetUnreadNotificationCount {
    unreadNotificationCount
  }
`;

// Messaging Queries
export const GET_MY_CONVERSATIONS = gql`
  query GetMyConversations {
    myConversations {
      id
      createdAt
      updatedAt
      unreadCount
      lastMessage {
        id
        body
        createdAt
        sender {
          id
          username
        }
      }
      otherParticipant {
        id
        username
        displayName
        profilePicture
        isVerified
      }
      ad {
        id
        title
        slug
        primaryImage
      }
    }
  }
`;

export const GET_CONVERSATION_MESSAGES = gql`
  query GetConversationMessages($conversationId: ID!, $limit: Int, $offset: Int) {
    conversationMessages(conversationId: $conversationId, limit: $limit, offset: $offset) {
      id
      body
      isRead
      createdAt
      sender {
        id
        username
        displayName
        profilePicture
      }
    }
  }
`;

export const GET_TOTAL_UNREAD_MESSAGES = gql`
  query GetTotalUnreadMessages {
    totalUnreadMessages
  }
`;

export const GET_TRENDING_VIDEOS = gql`
  query GetTrendingVideos($limit: Int) {
    trendingVideos(limit: $limit) {
      id
      title
      slug
      price
      priceType
      primaryImage
      videoUrl
      videoThumbnail
      videoDuration
      hasVideo
      location
      city
      condition
      isFeatured
      isTrending
      publishedAt
      viewCount
      favoriteCount
      videoViews
      user {
        id
        username
        displayName
        profilePicture
      }
      category {
        id
        name
        slug
      }
    }
  }
`;
